/* global axios */
import ApiClient from './ApiClient';

class FollowUpTemplatesAPI extends ApiClient {
  constructor() {
    super('follow-up-templates', { accountScoped: true });
  }

  get() {
    return axios.get(this.url);
  }

  show(templateId) {
    return axios.get(`${this.url}/${templateId}`);
  }

  create(data, attachments = []) {
    if (attachments.length > 0) {
      return this.createWithAttachments(data, attachments);
    }
    return axios.post(this.url, { follow_up_template: data });
  }

  createWithAttachments(data, attachments) {
    const formData = new FormData();
    formData.append('follow_up_template[name]', data.name);
    formData.append('follow_up_template[content]', data.content);
    if (data.active !== undefined) {
      formData.append('follow_up_template[active]', data.active);
    }
    attachments.forEach(file => {
      formData.append('attachments[]', file);
    });
    return axios.post(this.url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  update(templateId, data, attachments = []) {
    if (attachments.length > 0) {
      return this.updateWithAttachments(templateId, data, attachments);
    }
    return axios.patch(`${this.url}/${templateId}`, {
      follow_up_template: data,
    });
  }

  updateWithAttachments(templateId, data, attachments) {
    const formData = new FormData();
    if (data.name) formData.append('follow_up_template[name]', data.name);
    if (data.content)
      formData.append('follow_up_template[content]', data.content);
    if (data.active !== undefined) {
      formData.append('follow_up_template[active]', data.active);
    }
    attachments.forEach(file => {
      formData.append('attachments[]', file);
    });
    return axios.patch(`${this.url}/${templateId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  delete(templateId) {
    return axios.delete(`${this.url}/${templateId}`);
  }

  deleteAttachment(templateId, attachmentId) {
    return axios.delete(
      `${this.url}/${templateId}/attachments/${attachmentId}`
    );
  }

  preview(templateId, context = {}) {
    return axios.post(`${this.url}/${templateId}/preview`, { context });
  }

  getVariables() {
    return axios.get(`${this.url}/variables`);
  }

  // Template Items API methods

  getItems(templateId) {
    return axios.get(`${this.url}/${templateId}/items`);
  }

  getItem(templateId, itemId) {
    return axios.get(`${this.url}/${templateId}/items/${itemId}`);
  }

  createItem(templateId, data, attachment = null) {
    if (attachment) {
      return this.createItemWithAttachment(templateId, data, attachment);
    }
    return axios.post(`${this.url}/${templateId}/items`, {
      follow_up_template_item: data,
    });
  }

  createItemWithAttachment(templateId, data, attachment) {
    const formData = new FormData();
    formData.append('follow_up_template_item[position]', data.position || 0);
    formData.append(
      'follow_up_template_item[delay_seconds]',
      data.delay_seconds || 0
    );
    formData.append('follow_up_template_item[item_type]', data.item_type);
    if (data.content) {
      formData.append('follow_up_template_item[content]', data.content);
    }
    formData.append('attachment', attachment);
    return axios.post(`${this.url}/${templateId}/items`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  updateItem(templateId, itemId, data, attachment = null) {
    if (attachment) {
      return this.updateItemWithAttachment(
        templateId,
        itemId,
        data,
        attachment
      );
    }
    return axios.patch(`${this.url}/${templateId}/items/${itemId}`, {
      follow_up_template_item: data,
    });
  }

  updateItemWithAttachment(templateId, itemId, data, attachment) {
    const formData = new FormData();
    if (data.position !== undefined) {
      formData.append('follow_up_template_item[position]', data.position);
    }
    if (data.delay_seconds !== undefined) {
      formData.append(
        'follow_up_template_item[delay_seconds]',
        data.delay_seconds
      );
    }
    if (data.item_type) {
      formData.append('follow_up_template_item[item_type]', data.item_type);
    }
    if (data.content !== undefined) {
      formData.append('follow_up_template_item[content]', data.content);
    }
    formData.append('attachment', attachment);
    return axios.patch(`${this.url}/${templateId}/items/${itemId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  deleteItem(templateId, itemId) {
    return axios.delete(`${this.url}/${templateId}/items/${itemId}`);
  }

  reorderItems(templateId, items) {
    return axios.post(`${this.url}/${templateId}/items/reorder`, { items });
  }
}

export default new FollowUpTemplatesAPI();
